import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sunrise,
  RefreshCw,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Eye,
  Scale,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URGENCY_STYLES = {
  URGENT: "bg-red-500/20 text-red-300 border-red-500/40",
  HIGH: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  MEDIUM: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  LOW: "bg-blue-500/20 text-blue-300 border-blue-500/40",
};

const ACTION_ICONS = {
  BUY: TrendingUp,
  SELL: TrendingDown,
  TRIM: TrendingDown,
  HOLD: Eye,
  REVIEW: Eye,
  REBALANCE: Scale,
};

const ACTION_COLORS = {
  BUY: "text-green-400",
  SELL: "text-red-400",
  TRIM: "text-orange-400",
  HOLD: "text-blue-400",
  REVIEW: "text-purple-400",
  REBALANCE: "text-cyan-400",
};

const ActionCard = ({ action, onDone, onDismiss }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = ACTION_ICONS[action.action_type] || Eye;
  const colorClass = ACTION_COLORS[action.action_type] || "text-gray-400";

  return (
    <div
      data-testid={`daily-action-card-${action.id}`}
      className="rounded-lg border border-border bg-card/40 hover:bg-card/60 transition-all p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`mt-0.5 ${colorClass}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge
                variant="outline"
                className={`text-xs px-2 py-0 ${URGENCY_STYLES[action.urgency] || URGENCY_STYLES.MEDIUM}`}
              >
                {action.urgency}
              </Badge>
              <span className={`text-xs font-bold ${colorClass}`}>
                {action.action_type}
              </span>
              {action.symbol && (
                <span className="text-xs font-mono text-muted-foreground">
                  {action.symbol}
                </span>
              )}
              <span className="text-xs text-muted-foreground truncate">
                · {action.portfolio_name}
              </span>
            </div>
            <p className="text-sm font-medium text-foreground leading-snug mb-1">
              {action.headline}
            </p>
            {expanded && (
              <div className="mt-2 space-y-2 text-xs text-muted-foreground">
                <p>
                  <span className="font-semibold text-foreground">Why: </span>
                  {action.reason}
                </p>
                {action.macro_trigger && (
                  <p>
                    <span className="font-semibold text-foreground">Macro trigger: </span>
                    {action.macro_trigger}
                  </p>
                )}
                {action.expected_impact && (
                  <p>
                    <span className="font-semibold text-foreground">Expected impact: </span>
                    {action.expected_impact}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <button
          data-testid={`daily-action-toggle-${action.id}`}
          onClick={() => setExpanded(!expanded)}
          className="text-muted-foreground hover:text-foreground shrink-0"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {expanded && (
        <div className="mt-3 flex items-center gap-2 pt-2 border-t border-border/50">
          <Button
            data-testid={`daily-action-done-${action.id}`}
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => onDone(action.id)}
          >
            <Check className="w-3 h-3 mr-1" /> Mark Done
          </Button>
          <Button
            data-testid={`daily-action-dismiss-${action.id}`}
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-muted-foreground"
            onClick={() => onDismiss(action.id)}
          >
            <X className="w-3 h-3 mr-1" /> Dismiss
          </Button>
        </div>
      )}
    </div>
  );
};

export const DailyBrief = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [date, setDate] = useState("");

  const fetchActions = async () => {
    try {
      const res = await axios.get(`${API}/daily-actions`);
      const active = (res.data.actions || []).filter((a) => a.status === "active");
      setActions(active);
      setDate(res.data.date);
    } catch (e) {
      console.error("Failed to fetch daily actions", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActions();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/daily-actions/generate?force=true`);
      await fetchActions();
    } catch (e) {
      console.error("Generate failed", e);
    } finally {
      setGenerating(false);
    }
  };

  const handleDone = async (id) => {
    try {
      await axios.post(`${API}/daily-actions/${id}/done`);
      setActions((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await axios.post(`${API}/daily-actions/${id}/dismiss`);
      setActions((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const urgentCount = actions.filter((a) => a.urgency === "URGENT" || a.urgency === "HIGH").length;

  return (
    <Card data-testid="daily-brief-card" className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Sunrise className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                Today's Action Brief
                {urgentCount > 0 && (
                  <Badge className="bg-red-500/20 text-red-300 border-red-500/40 text-xs">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {urgentCount} urgent
                  </Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                AI-generated portfolio actions · {date}
              </p>
            </div>
          </div>
          <Button
            data-testid="daily-brief-refresh-btn"
            size="sm"
            variant="outline"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading today's brief...
          </div>
        ) : actions.length === 0 ? (
          <div data-testid="daily-brief-empty" className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-2">No active actions today.</p>
            <p className="text-xs text-muted-foreground/70">
              Tap Refresh to generate a fresh brief based on current macro conditions.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {actions.map((a) => (
              <ActionCard key={a.id} action={a} onDone={handleDone} onDismiss={handleDismiss} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
